import { useEffect, useMemo, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { glideTo, pose, SIGN, solids } from "./Player";
import { hallMaterial } from "./ProjectionHall";
import { BENCH_Z, BOOK, BOOK_SIGN } from "../../lib/layout";
import { openFlipbook } from "../../lib/links";
import { regionAt } from "../../lib/world";
import { SECTIONS } from "../../lib/sections";

export const WALL = { z: -2.2, w: 4.4, h: 2.4, y: 1.55 };
const GAP = 0.45;
const FADE = { value: 0 };
const GLOW = new THREE.Color(SECTIONS.nature.tint).lerp(new THREE.Color("#FFFFFF"), 0.5);
const smooth = (t) => t * t * (3 - 2 * t);

const vert = `varying vec2 vUv; varying vec3 vW;
void main() { vUv = uv; vec4 w = modelMatrix * vec4(position, 1.0); vW = w.xyz; gl_Position = projectionMatrix * viewMatrix * w; }`;
const glowFrag = `uniform vec3 uColor; uniform float uAlpha; uniform float uFade; uniform vec2 uSize; uniform float uSoft; varying vec2 vUv;
void main() {
  vec2 p = (vUv - 0.5) * uSize;
  vec2 q = abs(p) - (uSize * 0.5 - uSoft);
  float a = 1.0 - smoothstep(0.0, uSoft, length(max(q, 0.0)));
  gl_FragColor = vec4(uColor, a * a * uAlpha * uFade);
  #include <colorspace_fragment>
}`;
const cardFrag = `uniform sampler2D uMap; uniform float uHalfW; uniform float uFade; varying vec2 vUv; varying vec3 vW;
void main() {
  vec3 c = texture2D(uMap, vUv).rgb;
  float side = 1.0 - smoothstep(uHalfW - 0.9, uHalfW - 0.08, abs(vW.x));
  vec2 b = min(vUv, 1.0 - vUv);
  gl_FragColor = vec4(c, side * smoothstep(0.0, 0.035, min(b.x, b.y)) * uFade);
  #include <colorspace_fragment>
}`;
const sparkVert = `attribute float aPhase; uniform float uT; uniform float uPx; varying float vA;
void main() {
  vec3 p = position;
  p.y += uT * 0.5 * (0.4 + aPhase);
  vec4 mv = modelViewMatrix * vec4(p, 1.0);
  float life = clamp(uT * 1.3 - aPhase * 0.6, 0.0, 1.0);
  vA = sin(life * 3.14159) * (0.55 + 0.45 * sin(uT * 9.0 + aPhase * 20.0));
  gl_PointSize = uPx * (0.6 + aPhase) / -mv.z;
  gl_Position = projectionMatrix * mv;
}`;
const sparkFrag = `varying float vA;
void main() {
  vec2 c = gl_PointCoord - 0.5;
  float arm = smoothstep(0.08, 0.0, min(abs(c.x), abs(c.y)));
  float a = arm * smoothstep(0.5, 0.04, max(abs(c.x), abs(c.y))) * vA;
  gl_FragColor = vec4(1.0, 0.93, 0.8, a);
}`;

// soft moving painting light, for the Memory Wall panel and the floor around the bench
const flowFrag = `uniform sampler2D uA, uB; uniform float uMix, uT, uFade, uAlpha, uPanel; varying vec2 vUv;
void main() {
  vec2 q = vUv * vec2(1.6, 1.0);
  vec2 w = vec2(sin(q.y * 6.0 + uT * 0.25), sin(q.x * 5.0 - uT * 0.2)) * 0.05;
  vec2 uv = abs(fract(q * 0.5 + vec2(uT * 0.01, uT * 0.006) + w) * 2.0 - 1.0);
  vec3 c = mix(texture2D(uA, uv).rgb, texture2D(uB, uv).rgb, uMix);
  float swirl = smoothstep(-0.4, 0.9, sin(uv.x * 9.0 + uv.y * 5.0 + uT * 0.12 + w.x * 20.0));
  vec2 e = min(vUv, 1.0 - vUv);
  float edge = smoothstep(0.0, 0.3, min(e.x * 1.4, e.y));
  if (uPanel > 0.5) {
    float v = 1.0 - length((vUv - 0.5) * vec2(1.1, 1.5));
    vec3 base = mix(vec3(0.2, 0.15, 0.3), vec3(0.33, 0.26, 0.44), clamp(v, 0.0, 1.0));
    gl_FragColor = vec4(mix(base, c * 0.85, 0.22 + 0.16 * swirl), 0.94 * uFade);
  } else gl_FragColor = vec4(mix(c, vec3(1.0, 0.96, 0.9), 0.3), edge * edge * (0.3 + 0.35 * swirl) * uAlpha * uFade);
  #include <colorspace_fragment>
}`;
const flowMat = (flow, panel) =>
  new THREE.ShaderMaterial({ transparent: true, depthWrite: false, vertexShader: vert, fragmentShader: flowFrag, uniforms: { ...flow, uFade: FADE, uAlpha: { value: 1 }, uPanel: { value: panel ? 1 : 0 } } });
function useFlow(paintings, texById) {
  const flow = useMemo(() => {
    const ts = paintings.map((p) => texById[p.id]).filter(Boolean).slice(0, 6);
    return { uA: { value: ts[0] || null }, uB: { value: ts[1] || ts[0] || null }, uMix: { value: 0 }, uT: { value: 0 }, list: ts, i: 1 };
  }, [paintings, texById]);
  useFrame(() => {
    const t = performance.now() / 1000;
    flow.uT.value = t;
    const n = flow.list.length;
    if (n < 2) return;
    const k = Math.floor(t / 22), f = (t / 22) % 1;
    flow.uA.value = flow.list[k % n];
    flow.uB.value = flow.list[(k + 1) % n];
    flow.uMix.value = THREE.MathUtils.smoothstep(f, 0.75, 1);
  });
  return flow;
}

const glowMat = (color, size, soft, alpha, fade = FADE) =>
  new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    vertexShader: vert,
    fragmentShader: glowFrag,
    uniforms: { uColor: { value: new THREE.Color(color) }, uAlpha: { value: alpha }, uFade: fade, uSize: { value: new THREE.Vector2(...size) }, uSoft: { value: soft } },
  });
const cardMat = (map) =>
  new THREE.ShaderMaterial({ transparent: true, depthWrite: false, vertexShader: vert, fragmentShader: cardFrag, uniforms: { uMap: { value: map }, uHalfW: { value: WALL.w / 2 }, uFade: FADE } });

function buildRows(paintings, texById) {
  const list = paintings.filter((p) => texById[p.id]);
  const width = (cs) => cs.reduce((s, c) => s + c.w + GAP, 0);
  return [0, 1]
    .map((ri) => {
      const items = list.filter((_, i) => i % 2 === ri);
      if (!items.length) return null;
      const h = ri ? 0.92 : 1.04;
      const one = items.map((p) => {
        const t = texById[p.id];
        const a = t.image?.width ? t.image.width / t.image.height : p.aspect_ratio || 1;
        return { t, w: Math.min(1.9, h * a), h };
      });
      let cards = [...one];
      while (width(cards) < WALL.w + 2.6) cards = cards.concat(one);
      let x = 0;
      const placed = cards.map((c) => {
        const o = { ...c, off: x, mat: cardMat(c.t) };
        x += c.w + GAP;
        return o;
      });
      return { y: ri ? 2.12 : 1.02, z: WALL.z + 0.02 + ri * 0.005, span: x, cards: placed, dir: ri ? -1 : 1, speed: ri ? 0.05 : 0.065 };
    })
    .filter(Boolean);
}

function Montage({ paintings, texById }) {
  const rows = useMemo(() => buildRows(paintings, texById), [paintings, texById]);
  const refs = useRef([]);
  useFrame(() => {
    const t = performance.now() / 1000;
    let k = 0;
    rows.forEach((r) =>
      r.cards.forEach((c) => {
        const m = refs.current[k++];
        if (m) m.position.x = ((((c.off + r.dir * r.speed * t) % r.span) + r.span) % r.span) - r.span / 2 + c.w / 2;
      }),
    );
  });
  let k = 0;
  return rows.map((r, ri) =>
    r.cards.map((c, ci) => {
      const idx = k++;
      return (
        <mesh key={`${ri}-${ci}`} ref={(m) => (refs.current[idx] = m)} position={[0, r.y, r.z]} material={c.mat}>
          <planeGeometry args={[c.w, c.h]} />
        </mesh>
      );
    }),
  );
}

function MemoryWall({ paintings, texById, flow }) {
  const m = useMemo(
    () => ({
      panel: flowMat(flow, true),
      frame: new THREE.MeshBasicMaterial({ color: "#3A2B4D", transparent: true, toneMapped: false }),
    }),
    [flow],
  );
  useFrame(() => (m.frame.opacity = FADE.value));
  const { w, h, y, z } = WALL;
  return (
    <group>
      <mesh position={[0, y, z]} material={m.panel} renderOrder={-1}>
        <planeGeometry args={[w, h]} />
      </mesh>
      {[[0, y + h / 2, w + 0.12, 0.05], [0, y - h / 2, w + 0.12, 0.05]].map(([x, yy, bw, bh], i) => (
        <mesh key={`h${i}`} position={[x, yy, z]} material={m.frame}>
          <boxGeometry args={[bw, bh, 0.05]} />
        </mesh>
      ))}
      {[-w / 2, w / 2].map((x) => (
        <mesh key={`v${x}`} position={[x, y, z]} material={m.frame}>
          <boxGeometry args={[0.05, h + 0.12, 0.05]} />
        </mesh>
      ))}
      {[-(w / 2 - 0.4), w / 2 - 0.4].map((x) => (
        <mesh key={`p${x}`} position={[x, (y - h / 2) / 2, z]} material={m.frame}>
          <boxGeometry args={[0.06, y - h / 2, 0.06]} />
        </mesh>
      ))}
      <Montage paintings={paintings} texById={texById} />
    </group>
  );
}

function signTexture(title = "The Memory Wall", sub = "sit for a while") {
  const c = document.createElement("canvas");
  c.width = 1024;
  c.height = 560;
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 4;
  const draw = () => {
    const g = c.getContext("2d");
    g.clearRect(0, 0, 1024, 560);
    g.fillStyle = "rgba(255,251,245,0.5)";
    g.beginPath();
    g.roundRect(12, 12, 1000, 536, 34);
    g.fill();
    for (let i = 0; i < 900; i++) {
      g.fillStyle = `rgba(150,110,140,${Math.random() * 0.08})`;
      g.fillRect(Math.random() * 1024, Math.random() * 560, 2, 2);
    }
    g.strokeStyle = "rgba(186,104,200,0.35)";
    g.lineWidth = 4;
    g.beginPath();
    g.roundRect(42, 42, 940, 476, 24);
    g.stroke();
    g.textAlign = "center";
    g.textBaseline = "middle";
    g.fillStyle = "#4A2D55";
    g.font = '600 150px "Caveat"';
    g.font = `600 ${title.length > 16 ? 112 : 150}px "Caveat"`;
    g.fillText(title, 512, 240);
    g.strokeStyle = "#C27BA0";
    g.lineWidth = 5;
    g.lineCap = "round";
    g.beginPath();
    g.moveTo(290, 340);
    g.bezierCurveTo(420, 378, 600, 316, 734, 348);
    g.stroke();
    g.font = 'italic 500 52px "Cormorant Garamond"';
    g.fillStyle = "#7E57C2";
    g.fillText(sub, 512, 425);
    tex.needsUpdate = true;
  };
  draw();
  Promise.all(['600 150px "Caveat"', 'italic 500 52px "Cormorant Garamond"'].map((f) => document.fonts.load(f)))
    .then(draw)
    .catch(() => {});
  return tex;
}

function Signpost({ enabled, onReveal, onHot = () => {}, u, at = SIGN, title, sub, dwell = true, testid }) {
  const m = useMemo(() => {
    const tex = signTexture(title, sub);
    return {
      paper: hallMaterial(u, 0, { pale: 0.1, over: tex }),
      wood: hallMaterial(u, 0, { pale: 0.12 }),
      glow: glowMat(GLOW, [1.8, 1.2], 0.34, 0.12, { value: 1 }),
    };
  }, [u, title, sub]);
  const st = useRef({ armed: false, dwell: 0, near: false, hover: false, hot: false, glow: 0 });
  const sync = () => {
    const s = st.current;
    const hot = enabled && (s.near || s.hover);
    if (hot !== s.hot) {
      s.hot = hot;
      onHot(hot);
    }
  };
  useFrame((_, dt) => {
    const s = st.current;
    s.glow += ((s.hover || s.near ? 1 : 0) - s.glow) * Math.min(1, dt * 3);
    const breathe = 0.04 * Math.sin((performance.now() / 1000) * 1.4);
    m.paper.uniforms.uPale.value = 0.1 + 0.06 * s.glow + breathe * 0.5;
    m.glow.uniforms.uAlpha.value = 0.08 + 0.14 * s.glow + breathe * 0.5;
    if (!enabled || !dwell) {
      s.dwell = 0;
      s.near = false;
      return sync();
    }
    const d = Math.hypot(pose.x - at.x, pose.z - at.z);
    if (d > 2.2) s.armed = true;
    s.near = d < 2.2;
    s.dwell = s.armed && d < 1.35 ? s.dwell + dt : 0;
    sync();
    if (s.dwell > 0.4) {
      s.armed = false;
      s.dwell = 0;
      onReveal();
    }
  });
  const hover = (v) => {
    st.current.hover = v;
    sync();
  };
  return (
    <group
      position={[at.x, 0, at.z]}
      rotation={[0, at.rot, 0]}
      userData={testid ? { testid } : undefined}
      onClick={(e) => {
        e.stopPropagation();
        if (enabled && e.delta <= 8) onReveal();
      }}
      onPointerOver={(e) => {
        e.stopPropagation();
        hover(true);
      }}
      onPointerOut={() => hover(false)}
    >
      <mesh position={[0, 0.72, -0.03]} material={m.wood}>
        <boxGeometry args={[0.07, 1.44, 0.07]} />
      </mesh>
      <mesh position={[0, 0.025, -0.03]} material={m.wood}>
        <boxGeometry args={[0.42, 0.05, 0.3]} />
      </mesh>
      <mesh position={[0, 1.44, -0.05]} material={m.glow}>
        <planeGeometry args={[1.8, 1.2]} />
      </mesh>
      <mesh position={[0, 1.44, -0.01]} rotation={[0, 0, 0.025]} material={m.wood}>
        <boxGeometry args={[1.24, 0.7, 0.04]} />
      </mesh>
      <mesh position={[0, 1.44, 0.012]} rotation={[0, 0, 0.025]} material={m.paper}>
        <planeGeometry args={[1.14, 0.62]} />
      </mesh>
    </group>
  );
}

// The Flipbook: an open book of her paintings on a small walnut table beside the Memory Wall, under a reading lamp; a tap opens /flipbook in a new tab
const PUB = process.env.PUBLIC_URL || "";
const loadTex = (url, srgb = true) => {
  const t = new THREE.TextureLoader().load(`${PUB}${url}`);
  if (srgb) t.colorSpace = THREE.SRGBColorSpace;
  return t;
};
function bookTexture(left, right) {
  const c = document.createElement("canvas");
  c.width = 1024;
  c.height = 672;
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 4;
  const fit = (g, img, cx, cy, mw, mh) => {
    if (!img?.width) return;
    const s = Math.min(mw / img.width, mh / img.height);
    const w = img.width * s;
    const h = img.height * s;
    g.fillStyle = "rgba(60,40,30,0.25)";
    g.fillRect(cx - w / 2 + 6, cy - h / 2 + 8, w, h);
    g.drawImage(img, cx - w / 2, cy - h / 2, w, h);
  };
  const draw = () => {
    const g = c.getContext("2d");
    const paper = g.createLinearGradient(0, 0, 1024, 0);
    [[0, "#E9DCC2"], [0.06, "#F8EFDD"], [0.45, "#FBF4E6"], [0.5, "#CDB894"], [0.55, "#FBF4E6"], [0.94, "#F8EFDD"], [1, "#E9DCC2"]].forEach(([o, col]) => paper.addColorStop(o, col));
    g.fillStyle = paper;
    g.fillRect(0, 0, 1024, 672);
    for (let i = 0; i < 1600; i++) {
      g.fillStyle = `rgba(140,110,80,${Math.random() * 0.05})`;
      g.fillRect(Math.random() * 1024, Math.random() * 672, 2, 2);
    }
    fit(g, left, 256, 320, 400, 520);
    fit(g, right, 768, 230, 340, 300);
    g.textAlign = "center";
    g.textBaseline = "middle";
    g.fillStyle = "#4A2D55";
    g.font = '600 88px "Caveat"';
    g.fillText("The Flipbook", 768, 470);
    g.font = 'italic 500 34px "Cormorant Garamond"';
    g.fillStyle = "#7E57C2";
    g.fillText("32 pages of stories · tap to open", 768, 545);
    tex.needsUpdate = true;
  };
  draw();
  Promise.all(['600 88px "Caveat"', 'italic 500 34px "Cormorant Garamond"'].map((f) => document.fonts.load(f)))
    .then(draw)
    .catch(() => {});
  return { tex, draw };
}
// a page that lies open: flat by the spine, lifting and curling a little toward its outer edge
function pageGeometry(side) {
  const geo = new THREE.PlaneGeometry(0.32, 0.42, 18, 1);
  const p = geo.attributes.position;
  for (let i = 0; i < p.count; i++) {
    const u = (p.getX(i) + 0.16) / 0.32;
    const out = side < 0 ? 1 - u : u;
    p.setZ(i, 0.006 + 0.012 * Math.sin(Math.min(1, out * 1.6) * Math.PI * 0.5) + 0.03 * out ** 4);
  }
  geo.computeVertexNormals();
  return geo;
}
function radialTexture(stops) {
  const c = document.createElement("canvas");
  c.width = c.height = 128;
  const g = c.getContext("2d");
  const r = g.createRadialGradient(64, 64, 0, 64, 64, 64);
  stops.forEach(([o, col]) => r.addColorStop(o, col));
  g.fillStyle = r;
  g.fillRect(0, 0, 128, 128);
  return new THREE.CanvasTexture(c);
}

const WARM = new THREE.Color("#FFC98A");
function FlipbookTable({ enabled, onHot = () => {}, paintings, texById, at = BOOK }) {
  const pick = (slug, n) => paintings.find((p) => p.slug === slug) || paintings[n];
  const left = texById[pick("love-must-feel-like-this", 0)?.id]?.image;
  const right = texById[pick("the-lotus-pond", 1)?.id]?.image;
  const m = useMemo(() => {
    const { tex: spread, draw } = bookTexture(left, right);
    const half = (o) => {
      const t = spread.clone();
      t.repeat.set(0.5, 1);
      t.offset.set(o, 0);
      return t;
    };
    const lt = half(0);
    const rt = half(0.5);
    const keep = () => (lt.needsUpdate = rt.needsUpdate = true);
    document.fonts?.ready.then(() => setTimeout(() => (draw(), keep()), 300));
    const walnut = loadTex("/hall/book/walnut.webp");
    walnut.wrapS = walnut.wrapT = THREE.RepeatWrapping;
    const petals = loadTex("/hall/book/petals.webp");
    const soft = { transparent: true, depthWrite: false, toneMapped: false };
    return {
      left: new THREE.MeshStandardMaterial({ map: lt, roughness: 0.9, side: THREE.DoubleSide }),
      right: new THREE.MeshStandardMaterial({ map: rt, roughness: 0.9, side: THREE.DoubleSide }),
      leftGeo: pageGeometry(-1),
      rightGeo: pageGeometry(1),
      edge: new THREE.MeshStandardMaterial({ color: "#EADFC8", roughness: 0.95 }),
      board: new THREE.MeshStandardMaterial({ color: "#6E2F3C", roughness: 0.6 }),
      wood: new THREE.MeshStandardMaterial({ map: walnut, color: "#E2B48E", roughness: 0.48, metalness: 0.02 }),
      brass: new THREE.MeshStandardMaterial({ color: "#B88A4A", roughness: 0.35, metalness: 0.8 }),
      bulb: new THREE.MeshBasicMaterial({ color: "#FFE2B0", toneMapped: false }),
      shade: new THREE.MeshStandardMaterial({ color: "#F1D7B4", emissive: "#C77A3A", emissiveIntensity: 0.55, roughness: 0.9, side: THREE.DoubleSide }),
      leaf: new THREE.MeshStandardMaterial({ color: "#FBF4E6", roughness: 0.9, side: THREE.DoubleSide }),
      petals: new THREE.MeshStandardMaterial({ map: petals, transparent: true, depthWrite: false, roughness: 0.8 }),
      shadow: new THREE.MeshBasicMaterial({ map: radialTexture([[0, "rgba(30,16,10,0.55)"], [0.55, "rgba(30,16,10,0.22)"], [1, "rgba(30,16,10,0)"]]), ...soft }),
      pool: new THREE.MeshBasicMaterial({ map: radialTexture([[0, "rgba(255,196,128,0.5)"], [0.5, "rgba(255,186,120,0.16)"], [1, "rgba(255,186,120,0)"]]), blending: THREE.AdditiveBlending, ...soft }),
      wash: new THREE.MeshBasicMaterial({ map: radialTexture([[0, "rgba(255,186,140,0.62)"], [0.55, "rgba(238,150,140,0.24)"], [1, "rgba(236,150,150,0)"]]), blending: THREE.AdditiveBlending, ...soft }),
      glow: glowMat(WARM, [1.6, 1.6], 0.7, 0.2, { value: 1 }),
    };
  }, [left, right]);
  useEffect(() => () => Object.values(m).forEach((x) => x.dispose?.()), [m]);
  const st = useRef({ near: false, hover: false, hot: false, glow: 0 });
  const leaf = useRef(null);
  const sync = () => {
    const s = st.current;
    if (!enabled) return;
    const hot = s.near || s.hover;
    if (hot !== s.hot) {
      s.hot = hot;
      onHot(hot);
    }
  };
  useFrame((_, dt) => {
    const s = st.current;
    s.near = enabled && Math.hypot(pose.x - at.x, pose.z - at.z) < 2.4;
    s.glow += ((s.hover || s.near ? 1 : 0) - s.glow) * Math.min(1, dt * 3);
    const breathe = 0.5 + 0.5 * Math.sin((performance.now() / 1000) * 1.3);
    m.glow.uniforms.uAlpha.value = 0.1 + 0.1 * s.glow + 0.04 * breathe;
    m.pool.opacity = 0.85 + 0.15 * s.glow;
    // every few seconds a page lifts and turns over, as if someone were leafing through it
    if (leaf.current) {
      const p = ((performance.now() / 1000) % 6.5) / 1.9;
      leaf.current.visible = p < 1;
      leaf.current.rotation.z = Math.PI * smooth(Math.min(1, p));
      leaf.current.position.y = 0.032 + 0.05 * Math.sin(Math.PI * Math.min(1, p));
    }
  });
  const hover = (v) => {
    st.current.hover = v;
    sync();
  };
  useFrame(sync);
  const flat = [-Math.PI / 2, 0, 0];
  return (
    <group
      position={[at.x, 0, at.z]}
      rotation={[0, at.rot, 0]}
      onClick={(e) => {
        e.stopPropagation();
        if (enabled && e.delta <= 8) openFlipbook("book");
      }}
      onPointerOver={(e) => {
        e.stopPropagation();
        hover(true);
      }}
      onPointerOut={() => hover(false)}
    >
      <pointLight position={[0.26, 1.18, 0.12]} color="#FFC07A" intensity={1.7} distance={2.8} decay={1.6} />
      <mesh rotation={flat} position={[0.05, 0.01, 0.02]} material={m.shadow} renderOrder={1}>
        <planeGeometry args={[1.3, 1.0]} />
      </mesh>
      <mesh rotation={flat} position={[0.1, 0.014, 0]} material={m.pool} renderOrder={2}>
        <planeGeometry args={[3.4, 2.8]} />
      </mesh>
      <mesh position={[-0.2, 1.5, 0.8]} material={m.wash} renderOrder={1}>
        <planeGeometry args={[3.6, 3.2]} />
      </mesh>
      <mesh rotation={[-Math.PI / 2, 0, 0.6]} position={[-0.55, 0.016, -0.32]} material={m.petals} renderOrder={3}>
        <planeGeometry args={[0.7, 0.34]} />
      </mesh>
      <mesh position={[0, 0.74, 0]} material={m.wood}>
        <boxGeometry args={[0.88, 0.05, 0.62]} />
      </mesh>
      <mesh position={[0, 0.7, 0]} material={m.wood}>
        <boxGeometry args={[0.8, 0.05, 0.54]} />
      </mesh>
      {[[-0.36, -0.23], [0.36, -0.23], [-0.36, 0.23], [0.36, 0.23]].map(([x, z]) => (
        <mesh key={`${x}${z}`} position={[x, 0.34, z]} material={m.wood}>
          <cylinderGeometry args={[0.024, 0.016, 0.68, 10]} />
        </mesh>
      ))}
      <group position={[0.3, 0.765, 0.18]}>
        <mesh position={[0, 0.005, 0]} material={m.brass}>
          <cylinderGeometry args={[0.06, 0.07, 0.012, 20]} />
        </mesh>
        <mesh position={[0, 0.2, 0]} material={m.brass}>
          <cylinderGeometry args={[0.007, 0.007, 0.4, 8]} />
        </mesh>
        <mesh position={[0, 0.41, 0]} material={m.shade}>
          <cylinderGeometry args={[0.035, 0.1, 0.09, 24, 1, true]} />
        </mesh>
        <mesh position={[0, 0.385, 0]} material={m.bulb}>
          <sphereGeometry args={[0.026, 12, 10]} />
        </mesh>
      </group>
      <mesh position={[0, 1.02, 0.12]} material={m.glow}>
        <planeGeometry args={[1.6, 1.6]} />
      </mesh>
      <group position={[-0.06, 0.773, -0.04]} rotation={[-0.18, 0.12, 0]}>
        <mesh position={[0, 0.008, 0]} material={m.board}>
          <boxGeometry args={[0.7, 0.016, 0.46]} />
        </mesh>
        <mesh position={[-0.165, 0.022, 0]} material={m.edge}>
          <boxGeometry args={[0.32, 0.014, 0.42]} />
        </mesh>
        <mesh position={[0.165, 0.022, 0]} material={m.edge}>
          <boxGeometry args={[0.32, 0.014, 0.42]} />
        </mesh>
        <mesh geometry={m.leftGeo} position={[-0.165, 0.026, 0]} rotation={flat} material={m.left} />
        <mesh geometry={m.rightGeo} position={[0.165, 0.026, 0]} rotation={flat} material={m.right} />
        <group ref={leaf} position={[0, 0.032, 0]} visible={false}>
          <mesh geometry={m.rightGeo} position={[0.165, 0, 0]} rotation={flat} material={m.leaf} />
        </group>
        <mesh rotation={[-Math.PI / 2, 0, -0.4]} position={[0.2, 0.06, 0.16]} material={m.petals} renderOrder={3}>
          <planeGeometry args={[0.2, 0.1]} />
        </mesh>
      </group>
      <mesh rotation={[-Math.PI / 2, 0, 2.1]} position={[0.32, 0.773, -0.2]} material={m.petals} renderOrder={3}>
        <planeGeometry args={[0.3, 0.15]} />
      </mesh>
    </group>
  );
}

// The draw-in: petals drifting and a soft light trail along the floor, from the hall to the book; only while in the hall
function petalTexture() {
  const c = document.createElement("canvas");
  c.width = 64;
  c.height = 96;
  const g = c.getContext("2d");
  const grd = g.createLinearGradient(0, 0, 0, 96);
  grd.addColorStop(0, "#F7B7C4");
  grd.addColorStop(0.6, "#F2A0AE");
  grd.addColorStop(1, "#E9C08E");
  g.fillStyle = grd;
  g.beginPath();
  g.moveTo(32, 92);
  g.bezierCurveTo(-6, 60, 6, 8, 32, 4);
  g.bezierCurveTo(58, 8, 70, 60, 32, 92);
  g.fill();
  g.strokeStyle = "rgba(190,90,110,0.35)";
  g.lineWidth = 2;
  g.beginPath();
  g.moveTo(32, 86);
  g.quadraticCurveTo(28, 50, 32, 14);
  g.stroke();
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}
const TRAIL = [
  [0.7, 1.35, 1.0],
  [0.1, 1.2, 2.6],
  [-1.0, 1.05, 4.3],
  [-1.6, 0.95, 5.9],
  [BOOK.x + 0.15, 0.9, BOOK.z - 0.45],
];
const PETALS = 14;
const SPOTS = 7;
function FlipbookDrawIn({ enabled, children }) {
  const group = useRef(null);
  const petals = useRef([]);
  const spots = useRef([]);
  const m = useMemo(() => {
    const curve = new THREE.CatmullRomCurve3(TRAIL.map((p) => new THREE.Vector3(...p)));
    const tex = petalTexture();
    const petal = Array.from({ length: PETALS }, (_, i) => new THREE.MeshBasicMaterial({ map: tex, transparent: true, depthWrite: false, side: THREE.DoubleSide, toneMapped: false, color: new THREE.Color().setHSL(0.97 + (i % 3) * 0.015, 0.55, 0.88) }));
    const glow = radialTexture([[0, "rgba(255,200,140,0.7)"], [0.5, "rgba(255,190,130,0.25)"], [1, "rgba(255,190,130,0)"]]);
    const spot = Array.from({ length: SPOTS }, () => new THREE.MeshBasicMaterial({ map: glow, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, toneMapped: false }));
    const floor = Array.from({ length: SPOTS }, (_, i) => {
      const u = 0.08 + (i / (SPOTS - 1)) * 0.86;
      const p = curve.getPointAt(u);
      const d = curve.getTangentAt(u);
      return { x: p.x, z: p.z, rot: Math.atan2(d.x, d.z) };
    });
    return { curve, tex, glow, petal, spot, floor, seeds: Array.from({ length: PETALS }, () => Math.random()) };
  }, []);
  useEffect(() => () => [m.tex, m.glow, ...m.petal, ...m.spot].forEach((x) => x.dispose()), [m]);
  useFrame(() => {
    const show = enabled && regionAt(pose.x, pose.z) === "hall";
    if (group.current) group.current.visible = show;
    window.__afaeDrawIn = { visible: show };
    if (!show) return;
    const t = performance.now() / 1000;
    petals.current.forEach((p, i) => {
      if (!p) return;
      const s = m.seeds[i];
      const u = (t * 0.035 + i / PETALS + s * 0.05) % 1;
      const at = m.curve.getPointAt(u);
      p.position.set(at.x + 0.22 * Math.sin(t * 0.7 + s * 9), at.y + 0.12 * Math.sin(t * 0.9 + i) - 0.25 * u, at.z + 0.15 * Math.cos(t * 0.6 + s * 7));
      p.rotation.set(t * (0.6 + s) + i, t * (0.4 + s * 0.5), t * 0.3 + s * 6);
      m.petal[i].opacity = 0.9 * THREE.MathUtils.smoothstep(u, 0, 0.12) * (1 - THREE.MathUtils.smoothstep(u, 0.88, 1));
    });
    // a warm glow travels along the floor toward the book
    m.spot.forEach((mat, i) => (mat.opacity = 0.22 + 0.45 * Math.max(0, Math.sin(t * 1.3 - i * 0.65)) ** 2));
  });
  return (
    <group ref={group}>
      {m.petal.map((mat, i) => (
        <mesh key={i} ref={(r) => (petals.current[i] = r)} material={mat} renderOrder={4} frustumCulled={false}>
          <planeGeometry args={[0.06, 0.09]} />
        </mesh>
      ))}
      {m.floor.map((f, i) => (
        <mesh key={i} ref={(r) => (spots.current[i] = r)} position={[f.x, 0.013, f.z]} rotation={[-Math.PI / 2, 0, f.rot]} material={m.spot[i]} renderOrder={1}>
          <planeGeometry args={[0.55, 1.1]} />
        </mesh>
      ))}
      {children}
    </group>
  );
}

function Sparkles({ sparkRef }) {
  const { geo, mat } = useMemo(() => {
    const n = 80;
    const pos = new Float32Array(n * 3);
    const ph = new Float32Array(n);
    for (let i = 0; i < n; i++) {
      const onWall = i % 2 === 0;
      pos[i * 3] = (Math.random() - 0.5) * (onWall ? WALL.w + 0.4 : 3);
      pos[i * 3 + 1] = onWall ? 0.3 + Math.random() * 2.6 : 0.2 + Math.random() * 1.0;
      pos[i * 3 + 2] = onWall ? WALL.z + 0.15 : (Math.random() - 0.5) * 0.9;
      ph[i] = Math.random();
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.BufferAttribute(pos, 3));
    g.setAttribute("aPhase", new THREE.BufferAttribute(ph, 1));
    const m = new THREE.ShaderMaterial({ transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, vertexShader: sparkVert, fragmentShader: sparkFrag, uniforms: { uT: { value: 0 }, uPx: { value: 90 } } });
    return { geo: g, mat: m };
  }, []);
  return <points ref={sparkRef} geometry={geo} material={mat} visible={false} frustumCulled={false} />;
}

const SUBS = { love: "rose & lavender", nature: "a garden gallery", spirit: "the temple", thoughts: "a slow film", fire: "embers in a circle" };


export function BenchWall({ paintings, texById, u, on, enabled, onReveal, onRevealed, onHot, onBookHot }) {
  const flow = useFlow(paintings, texById);
  const m = useMemo(
    () => ({
      seat: hallMaterial(u, 0, { pale: 0.08, alpha: FADE }),
      leg: hallMaterial(u, 0, { pale: 0.04, alpha: FADE }),
      floor: glowMat(GLOW, [4.2, 2.4], 0.8, 0.15),
      floorLight: flowMat(flow, false),
    }),
    [u, flow],
  );
  const group = useRef(null);
  const spark = useRef(null);
  const st = useRef({ f: 0, t: 0, done: false });

  useFrame(() => {
    const s = st.current;
    const now = performance.now();
    const step = s.last ? (now - s.last) / 1000 : 0;
    s.last = now;
    const pulse = 0.5 + 0.5 * Math.sin((now / 1000) * 1.2);
    m.seat.uniforms.uPale.value = 0.08 + 0.04 * pulse;
    m.floor.uniforms.uAlpha.value = 0.1 + 0.06 * pulse;
    if (on && s.f === 0) s.t = 0;
    if (on) s.t += step;
    else s.done = false;
    s.f = on ? Math.min(1, s.f + step / 2.6) : Math.max(0, s.f - step / 2.0);
    const e = smooth(s.f);
    FADE.value = e;
    solids.bench = s.f > 0.05;
    if (group.current) {
      group.current.visible = s.f > 0.001;
      group.current.position.y = -0.6 * (1 - e);
    }
    if (spark.current) {
      const u = s.t / 2.2;
      spark.current.visible = on && u < 1.7;
      spark.current.material.uniforms.uT.value = u;
    }
    window.__afaeReveal = { fade: +s.f.toFixed(2), on: !!on, seconds: +s.t.toFixed(2) };
    if (on && s.f >= 1 && !s.done) {
      s.done = true;
      onRevealed();
    }
  });

  return (
    <>
      <Signpost enabled={enabled} onReveal={onReveal} onHot={onHot} u={u} />
      <FlipbookTable enabled={enabled} onHot={onBookHot} paintings={paintings} texById={texById} />
      <FlipbookDrawIn enabled={enabled}>
        <Signpost enabled={enabled} onReveal={() => glideTo("flipbook")} u={u} at={BOOK_SIGN} title="The Flipbook" sub="stories of every painting ›" dwell={false} testid="flipbook-sign" />
      </FlipbookDrawIn>
      <group position={[0, 0, BENCH_Z]} rotation={[0, Math.PI, 0]}>
      <group ref={group} visible={false}>
        <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.01, 0]} material={m.floor}>
          <planeGeometry args={[4.2, 2.4]} />
        </mesh>
        <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.015, 0.1]} material={m.floorLight} renderOrder={1}>
          <planeGeometry args={[6.2, 3.6]} />
        </mesh>
        <mesh position={[0, 0.45, 0]} material={m.seat}>
          <boxGeometry args={[2.6, 0.09, 0.76]} />
        </mesh>
        <mesh position={[0, 0.8, 0.36]} material={m.seat}>
          <boxGeometry args={[2.6, 0.3, 0.06]} />
        </mesh>
        {[-1.05, 1.05].map((x) => (
          <group key={x}>
            <mesh position={[x, 0.2, 0]} material={m.leg}>
              <boxGeometry args={[0.08, 0.4, 0.62]} />
            </mesh>
            <mesh position={[x, 0.6, 0.36]} material={m.leg}>
              <boxGeometry args={[0.06, 0.32, 0.06]} />
            </mesh>
          </group>
        ))}
        <MemoryWall paintings={paintings} texById={texById} flow={flow} />
      </group>
      <Sparkles sparkRef={spark} />
      </group>
    </>
  );
}

